import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Text,
  Button,
  Alert,
  Group,
  NumberInput,
  Divider,
} from "@mantine/core";
import { notifySuccess } from "../../utils/notify";
import { IconGift, IconAlertCircle } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import { useResponsive } from "../../context/ResponsiveContext";
import { PackageReasonCategory } from "../../types/packages";
import ReasonFields, { isReasonValid } from "./ReasonFields";
import BrandedLoadingOverlay from "../common/BrandedLoadingOverlay";
import GiftCardCodeField from "../common/GiftCardCodeField";

/** The universal floor for a manually granted gift card. */
const UNIVERSAL_MIN_AMOUNT = 1;

interface AddGiftCardModalProps {
  opened: boolean;
  onClose: () => void;
  salonId: string;
  clientId: string;
  /** Refetch the client's gift cards after a successful grant. */
  onSuccess: () => void | Promise<void>;
}

/**
 * Manually grant a stored-value gift card to a client (the money-balance analog
 * of `AddPackageToClientModal`). Every grant records a ledger entry with a
 * required reason + note (mirrors the package grant's submit-gating UX). The
 * amount is floored at the universal $1 minimum.
 */
export default function AddGiftCardModal({
  opened,
  onClose,
  salonId,
  clientId,
  onSuccess,
}: AddGiftCardModalProps) {
  const { isMobile } = useResponsive();
  const { addClientGiftCard } = useApp();

  const [amount, setAmount] = useState<number | "">("");
  const [code, setCode] = useState("");
  // Empty is valid (auto-generate); a malformed/taken custom code flips this off.
  const [codeValid, setCodeValid] = useState(true);
  const [category, setCategory] = useState<PackageReasonCategory | null>(null);
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setAmount("");
      setCode("");
      setCodeValid(true);
      setCategory(null);
      setNote("");
      setError(null);
      setIsSubmitting(false);
    }
  }, [opened]);

  const amountIsNumber = typeof amount === "number";
  const validationError =
    amountIsNumber && amount < UNIVERSAL_MIN_AMOUNT
      ? `Enter an amount of at least $${UNIVERSAL_MIN_AMOUNT}.`
      : null;

  const canSubmit =
    amountIsNumber &&
    amount >= UNIVERSAL_MIN_AMOUNT &&
    codeValid &&
    isReasonValid(category, note) &&
    !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit || category === null || !amountIsNumber) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await addClientGiftCard(salonId, clientId, {
        amount,
        reason_category: category,
        note: note.trim(),
        // Blank => backend auto-generates a code.
        code: code.trim() || undefined,
      });
      notifySuccess("The gift card was added to the client.", {
        title: "Gift card added",
      });
      try {
        await Promise.resolve(onSuccess());
      } catch (e) {
        console.error("Error in add-gift-card success handler:", e);
      }
      onClose();
    } catch (err) {
      console.error("Error adding gift card:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to add the gift card. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Add Gift Card</Text>}
      size="md"
      fullScreen={isMobile}
      centered
      closeOnClickOutside={!isSubmitting}
      closeOnEscape={!isSubmitting}
    >
      <BrandedLoadingOverlay visible={isSubmitting} />

      <Stack gap="md">
        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {error}
          </Alert>
        )}

        <NumberInput
          label="Amount"
          placeholder="e.g. 50.00"
          value={amount}
          onChange={(value) =>
            setAmount(typeof value === "number" ? value : "")
          }
          min={0}
          decimalScale={2}
          prefix="$"
          withAsterisk
          disabled={isSubmitting}
          error={validationError}
        />

        <GiftCardCodeField
          salonId={salonId}
          value={code}
          onChange={setCode}
          onValidityChange={setCodeValid}
          disabled={isSubmitting}
        />

        <ReasonFields
          category={category}
          note={note}
          onCategoryChange={setCategory}
          onNoteChange={setNote}
          disabled={isSubmitting}
        />

        <Divider />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="var(--brand-teal)"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!canSubmit}
            leftSection={<IconGift size={18} />}
          >
            Add Gift Card
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
